import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  IconButton, TextField, Typography, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Autocomplete, Alert
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, PointOfSale as SalesIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import axiosInstance from '../../services/axiosInstance';
import { DailySale, DailySalePaymentDetail } from '../../types/dailySales.types';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';

interface Branch {
  branchId: number;
  branchCode: string;
  branchName: string;
  branchNameAr: string;
}

interface Concept {
  conceptId: number;
  conceptCode: string;
  conceptName: string;
  conceptNameAr: string;
  branchId: number;
}

interface PaymentMethod {
  paymentMethodId: number;
  methodCode: string;
  methodName: string;
  methodNameAr: string;
  isActive: boolean;
}

interface DialogProps {
  open: boolean;
  dailySale: DailySale | null;
  onClose: (refresh?: boolean) => void;
}

const DailySalesDialog: React.FC<DialogProps> = ({ open, dailySale, onClose }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [branches, setBranches] = useState<Branch[]>([]);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [formData, setFormData] = useState({
    dailySaleId: 0,
    branchId: 0,
    conceptId: 0,
    saleDate: new Date().toISOString().split('T')[0],
    grossSales: 0,
    discount: 0,
    serviceCharge: 0,
    deliveryAmount: 0,
    dineInAmount: 0,
    takeAwayAmount: 0,
    depositAmount: 0,
    bankName: '',
    depositDate: '',
    depositReference: '',
    cancelledOrders: 0,
    cancellationReason: '',
    notes: ''
  });
  const [paymentDetails, setPaymentDetails] = useState<DailySalePaymentDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const { minDate, maxDate } = useFiscalDateRange();

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const t = {
    title: dailySale ? (isRTL ? 'تعديل مبيعات يومية' : 'Edit Daily Sales') : (isRTL ? 'إضافة مبيعات يومية' : 'Add Daily Sales'),
    branch: isRTL ? 'الفرع' : 'Branch',
    concept: isRTL ? 'المفهوم' : 'Concept',
    saleDate: isRTL ? 'تاريخ المبيعات' : 'Sale Date',
    grossSales: isRTL ? 'إجمالي المبيعات' : 'Gross Sales',
    discount: isRTL ? 'الخصومات' : 'Discounts',
    serviceCharge: isRTL ? 'رسوم الخدمة' : 'Service Charge',
    netSales: isRTL ? 'صافي المبيعات' : 'Net Sales',
    salesTypes: isRTL ? 'أنواع المبيعات' : 'Sales Types',
    deliveryAmount: isRTL ? 'ديليفري' : 'Delivery',
    dineInAmount: isRTL ? 'داين إن' : 'Dine-In',
    takeAwayAmount: isRTL ? 'تيك أواي' : 'Take-Away',
    totalSalesTypes: isRTL ? 'مجموع الأنواع' : 'Total Types',
    notes: isRTL ? 'ملاحظات' : 'Notes',
    paymentDetails: isRTL ? 'تفاصيل طرق الدفع' : 'Payment Details',
    paymentMethod: isRTL ? 'طريقة الدفع' : 'Payment Method',
    amount: isRTL ? 'المبلغ' : 'Amount',
    addPayment: isRTL ? 'إضافة طريقة دفع' : 'Add Payment',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    saveSuccess: isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully',
    requiredFields: isRTL ? 'الفرع والمفهوم مطلوبان' : 'Branch and Concept are required',
    totalMismatch: isRTL ? 'مجموع طرق الدفع يجب أن يساوي صافي المبيعات' : 'Payment total must equal net sales',
    typesMismatch: isRTL ? 'مجموع أنواع المبيعات يجب أن يساوي صافي المبيعات' : 'Sales types total must equal net sales',
    actions: isRTL ? 'الإجراءات' : 'Actions',
    totalPayments: isRTL ? 'إجمالي المدفوعات' : 'Total Payments',
    difference: isRTL ? 'الفرق' : 'Difference'
  };

  useEffect(() => {
    if (open) {
      loadBranches();
      loadPaymentMethods();
      if (dailySale) {
        setFormData({
          dailySaleId: dailySale.dailySaleId,
          branchId: dailySale.branchId,
          conceptId: dailySale.conceptId,
          saleDate: dailySale.saleDate.split('T')[0],
          grossSales: dailySale.grossSales || 0,
          discount: dailySale.discount || 0,
          serviceCharge: dailySale.serviceCharge || 0,
          deliveryAmount: dailySale.deliveryAmount || 0,
          dineInAmount: dailySale.dineInAmount || 0,
          takeAwayAmount: dailySale.takeAwayAmount || 0,
          depositAmount: dailySale.depositAmount || 0,
          bankName: dailySale.bankName || '',
          depositDate: dailySale.depositDate?.split('T')[0] || '',
          depositReference: dailySale.depositReference || '',
          cancelledOrders: dailySale.cancelledOrders || 0,
          cancellationReason: dailySale.cancellationReason || '',
          notes: dailySale.notes || ''
        });
        setPaymentDetails(dailySale.paymentDetails || []);
        loadConcepts(dailySale.branchId);
      } else {
        resetForm();
      }
    }
  }, [open, dailySale]);

  const loadBranches = async () => {
    try {
      const response = await axiosInstance.get('/Branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadConcepts = async (branchId: number) => {
    try {
      const response = await axiosInstance.get(`/Concepts/branch/${branchId}`);
      setConcepts(response.data);
    } catch (error) {
      console.error('Failed to load concepts:', error);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const response = await axiosInstance.get('/PaymentMethods');
      setPaymentMethods(response.data.filter((m: PaymentMethod) => m.isActive));
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      dailySaleId: 0,
      branchId: 0,
      conceptId: 0,
      saleDate: new Date().toISOString().split('T')[0],
      grossSales: 0,
      discount: 0,
      serviceCharge: 0,
      deliveryAmount: 0,
      dineInAmount: 0,
      takeAwayAmount: 0,
      depositAmount: 0,
      bankName: '',
      depositDate: '',
      depositReference: '',
      cancelledOrders: 0,
      cancellationReason: '',
      notes: ''
    });
    setPaymentDetails([]);
  };

  const handleBranchChange = (branchId: number) => {
    setFormData({ ...formData, branchId, conceptId: 0 });
    setConcepts([]);
    if (branchId) loadConcepts(branchId);
  };

  const handleAddPaymentDetail = () => {
    setPaymentDetails([...paymentDetails, { paymentMethodId: 0, amount: 0 }]);
  };

  const handleRemovePaymentDetail = (index: number) => {
    setPaymentDetails(paymentDetails.filter((_, i) => i !== index));
  };

  const handlePaymentDetailChange = (index: number, field: 'paymentMethodId' | 'amount', value: any) => {
    const updated = [...paymentDetails];
    updated[index] = { ...updated[index], [field]: value };
    setPaymentDetails(updated);
  };

  const netSales = formData.grossSales - formData.discount + formData.serviceCharge;
  const totalPayments = paymentDetails.reduce((sum, detail) => sum + (detail.amount || 0), 0);
  const paymentDifference = netSales - totalPayments;
  const totalSalesTypes = formData.deliveryAmount + formData.dineInAmount + formData.takeAwayAmount;
  const typesDifference = netSales - totalSalesTypes;

  const handleSave = async () => {
    if (!formData.branchId || !formData.conceptId) {
      toast.error(t.requiredFields);
      return;
    }
    if (Math.abs(paymentDifference) > 0.01) {
      toast.error(t.totalMismatch);
      return;
    }
    if (Math.abs(typesDifference) > 0.01) {
      toast.error(t.typesMismatch);
      return;
    }
    try {
      setLoading(true);
      const payload = {
        dailySaleId: formData.dailySaleId,
        branchId: formData.branchId,
        conceptId: formData.conceptId,
        saleDate: formData.saleDate,
        grossSales: formData.grossSales,
        discount: formData.discount,
        serviceCharge: formData.serviceCharge,
        netSales: netSales,
        deliveryAmount: formData.deliveryAmount,
        dineInAmount: formData.dineInAmount,
        takeAwayAmount: formData.takeAwayAmount,
        depositAmount: formData.depositAmount,
        bankName: formData.bankName || null,
        depositDate: formData.depositDate || null,
        depositReference: formData.depositReference || null,
        cancelledOrders: formData.cancelledOrders,
        cancellationReason: formData.cancellationReason || null,
        notes: formData.notes || null,
        paymentDetails: paymentDetails.filter(d => d.paymentMethodId > 0 && d.amount > 0)
      };
      if (dailySale) {
        await axiosInstance.put(`/DailySales/${dailySale.dailySaleId}`, payload);
      } else {
        await axiosInstance.post('/DailySales', payload);
      }
      toast.success(t.saveSuccess);
      onClose(true);
    } catch (error: any) {
      console.error('Save error:', error.response?.data);
      toast.error(error.response?.data?.message || error.response?.data?.title || (isRTL ? 'فشلت العملية' : 'Operation failed'));
    } finally {
      setLoading(false);
    }
  };

  const selectedBranch = branches.find(b => b.branchId === formData.branchId);
  const selectedConcept = concepts.find(c => c.conceptId === formData.conceptId);

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#FF6F00', color: 'white', fontWeight: 'bold' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SalesIcon />
          {t.title}
        </Box>
        <IconButton onClick={() => onClose(false)} size="small" sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
            <Autocomplete
              options={branches}
              getOptionLabel={(option) => `${option.branchCode} - ${isRTL ? option.branchNameAr : option.branchName}`}
              value={selectedBranch || null}
              onChange={(_, newValue) => handleBranchChange(newValue?.branchId || 0)}
              renderInput={(params) => <TextField {...params} label={t.branch} required />}
              disabled={!!dailySale}
              fullWidth
            />
            <Autocomplete
              options={concepts}
              getOptionLabel={(option) => `${option.conceptCode} - ${isRTL ? option.conceptNameAr : option.conceptName}`}
              value={selectedConcept || null}
              onChange={(_, newValue) => setFormData({ ...formData, conceptId: newValue?.conceptId || 0 })}
              renderInput={(params) => <TextField {...params} label={t.concept} required />}
              disabled={!!dailySale || !formData.branchId}
              fullWidth
            />
          </Box>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.saleDate}
              value={formData.saleDate ? new Date(formData.saleDate) : null}
              onChange={(date) => setFormData({ ...formData, saleDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={maxDate}
              slotProps={{ textField: { required: true, fullWidth: true, sx: { mb: 3 } } }}
            />
          </LocalizationProvider>

          <Box sx={{ mb: 3, p: 2, border: '2px solid #FF6F00', borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#FF6F00' }}>{isRTL ? 'المبيعات' : 'Sales'}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <TextField
                label={t.grossSales}
                type="number"
                value={formData.grossSales}
                onChange={(e) => setFormData({ ...formData, grossSales: parseFloat(e.target.value) || 0 })}
                required
                fullWidth
                inputProps={{ min: 0, step: 0.001 }}
              />
              <TextField
                label={t.discount}
                type="number"
                value={formData.discount}
                onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                fullWidth
                inputProps={{ min: 0, step: 0.001 }}
              />
              <TextField
                label={t.serviceCharge}
                type="number"
                value={formData.serviceCharge}
                onChange={(e) => setFormData({ ...formData, serviceCharge: parseFloat(e.target.value) || 0 })}
                fullWidth
                inputProps={{ min: 0, step: 0.001 }}
              />
            </Box>
            <Box sx={{ mt: 2, p: 1.5, border: '2px solid #4CAF50', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" sx={{ color: '#4CAF50' }}>{t.netSales}</Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">{netSales.toFixed(3)} KD</Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3, p: 2, border: '2px solid #1976D2', borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#1976D2' }}>{t.salesTypes}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <TextField
                label={t.deliveryAmount}
                type="number"
                value={formData.deliveryAmount}
                onChange={(e) => setFormData({ ...formData, deliveryAmount: parseFloat(e.target.value) || 0 })}
                fullWidth
                inputProps={{ min: 0, step: 0.001 }}
              />
              <TextField
                label={t.dineInAmount}
                type="number"
                value={formData.dineInAmount}
                onChange={(e) => setFormData({ ...formData, dineInAmount: parseFloat(e.target.value) || 0 })}
                fullWidth
                inputProps={{ min: 0, step: 0.001 }}
              />
              <TextField
                label={t.takeAwayAmount}
                type="number"
                value={formData.takeAwayAmount}
                onChange={(e) => setFormData({ ...formData, takeAwayAmount: parseFloat(e.target.value) || 0 })}
                fullWidth
                inputProps={{ min: 0, step: 0.001 }}
              />
            </Box>
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box sx={{ p: 1.5, border: '2px solid #E65100', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ color: '#E65100' }}>{t.totalSalesTypes}</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#E65100' }}>{totalSalesTypes.toFixed(3)} KD</Typography>
              </Box>
              <Box sx={{ p: 1.5, border: typesDifference !== 0 ? '2px solid #D32F2F' : '2px solid #4CAF50', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ color: typesDifference !== 0 ? '#D32F2F' : '#4CAF50' }}>{t.difference}</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: typesDifference !== 0 ? '#D32F2F' : '#4CAF50' }}>{typesDifference.toFixed(3)} KD</Typography>
              </Box>
            </Box>
            {Math.abs(typesDifference) > 0.01 && (
              <Alert severity="error" sx={{ mt: 2 }}>{t.typesMismatch}</Alert>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#FF6F00' }}>{t.paymentDetails}</Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddPaymentDetail} sx={{ borderColor: '#FF6F00', color: '#FF6F00' }}>
                {t.addPayment}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box ref={gridRef}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#FF6F00' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.paymentMethod}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.amount}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentDetails.map((detail, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Autocomplete
                          options={paymentMethods}
                          getOptionLabel={(option) => `${option.methodCode} - ${isRTL ? option.methodNameAr : option.methodName}`}
                          value={paymentMethods.find(p => p.paymentMethodId === detail.paymentMethodId) || null}
                          onChange={(_, newValue) => handlePaymentDetailChange(index, 'paymentMethodId', newValue?.paymentMethodId || 0)}
                          renderInput={(params) => <TextField {...params} size="small" />}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={detail.amount}
                          onChange={(e) => handlePaymentDetailChange(index, 'amount', parseFloat(e.target.value) || 0)}
                          size="small"
                          fullWidth
                          inputProps={{ min: 0, step: 0.001 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleRemovePaymentDetail(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ borderTop: '2px solid #E65100' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#E65100' }}>{t.totalPayments}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#E65100' }}>{totalPayments.toFixed(3)} KD</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow sx={{ borderTop: '2px solid ' + (paymentDifference !== 0 ? '#D32F2F' : '#4CAF50') }}>
                    <TableCell sx={{ fontWeight: 'bold', color: paymentDifference !== 0 ? '#D32F2F' : '#4CAF50' }}>{t.difference}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: paymentDifference !== 0 ? '#D32F2F' : '#4CAF50' }}>
                      {paymentDifference.toFixed(3)} KD
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            </Box>

            {Math.abs(paymentDifference) > 0.01 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t.totalMismatch}
              </Alert>
            )}
          </Box>

          <TextField
            label={t.notes}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => onClose(false)} disabled={loading}>{t.cancel}</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} disabled={loading} sx={{ bgcolor: '#FF6F00', '&:hover': { bgcolor: '#E65100' } }}>
          {loading ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DailySalesDialog;